import React from 'react';
import { Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend
} from 'chart.js';

ChartJS.register(ArcElement, Tooltip, Legend);

export default function SocialMediaInfluence() {
  // Dynamic data structure for easy JSON implementation
  const pageData = {
    title: "Social Media Influence",
    pageNumber: 31,
    
    // Left side insight boxes
    insights: [
      {
        id: 1,
        backgroundColor: "#aee772",
        heading: "Social media influences a smaller group but the most valuable group",
        bullets: [
          "Only 1 in 5 dog owners has purchased dog food because of social media.",
          "That minority shapes category trends more than the average consumer."
        ]
      },
      {
        id: 2,
        backgroundColor: "#aee772",
        heading: "High alignment with growth drivers",
        columns: [
          {
            items: [
              "Try new foods",
              "Respond to premiums &",
              "Health claims"
            ]
          },
          {
            items: [
              "Notice new product",
              "Variety",
              "Reports strong satisfaction"
            ]
          }
        ]
      },
      {
        id: 3,
        backgroundColor: "#aee772",
        heading: "Younger & multi-dog owners are the digital core",
        columns: [
          {
            label: "Purchase:",
            items: [
              "30+ lbs dogs (36.5% say yes)",
              "Multi-dog households are strongest among"
            ]
          },
          {
            items: [
              "18-34 yrs (5.5x any age)",
              "Small-to-medium dogs",
              "Pet influencer followers (44.7% YES)",
              "Specialty brands (25.1%)"
            ]
          }
        ]
      },
      {
        id: 4,
        backgroundColor: "#aee772",
        heading: "Social media acts as a discovery engine, not a mass trigger",
        columns: [
          {
            label: "Digital first owners",
            subLabel: "Use social media for:",
            items: [
              "Product discovery",
              "Brand transparency"
            ]
          },
          {
            items: [
              "Create recommendations",
              "Share owned snapshots",
              "Opinions in search of fun/information"
            ]
          }
        ]
      }
    ],
    
    // Right side donut chart
    donutChart: {
      heading: "Have you EVER purchased dog food based on social media?",
      data: {
        yes: 22.1,
        no: 77.9
      }
    }
  };

  // Donut chart configuration
  const donutData = {
    labels: ['YES', 'NO'],
    datasets: [{
      data: [pageData.donutChart.data.yes, pageData.donutChart.data.no],
      backgroundColor: ['#aee772', '#D1D5DB'],
      borderWidth: 0,
      cutout: '70%'
    }]
  };

  const donutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        callbacks: {
          label: function(context:any) {
            return context.label + ': ' + context.parsed + '%';
          }
        }
      }
    }
  };

  return (
  <div className="flex flex-col h-full items-center w-full">
      <h1 className="relative inline-block text-white text-center font-extrabold 
  max-w-4xl text-2xl lg:text-4xl font-nunito mb-4 
  bg-gradient-to-r from-[#92D050] to-[#8CC54F] 
  px-6 py-2
  before:content-[''] before:absolute before:left-0 before:top-1/2 
  before:-translate-y-1/2 before:-translate-x-full
  before:border-y-[16px] before:border-r-[16px] before:border-y-transparent 
  before:border-r-[#92D050]

  after:content-[''] after:absolute after:right-0 after:top-1/2 
  after:-translate-y-1/2 after:translate-x-full
  after:border-y-[16px] after:border-l-[16px] after:border-y-transparent 
  after:border-l-[#8CC54F]">{pageData.title}</h1>
    

    
      <div className="grid grid-cols-2 gap-6">
        {/* Left Column - Insight Boxes */}
        <div className="space-y-1">
          {pageData.insights.map((insight) => (
            <div 
              key={insight.id}
              className={`border-2 border-green-700 bg-[${insight.backgroundColor}] rounded-xl p-2 shadow-sm text-sm leading-relaxed`}
            >
              <h2 className="text-sm font-bold mb-3 text-center">
                {insight.heading}
              </h2>
              
              {/* If it has bullets (simple list) */}
              {insight.bullets && (
                <ul className="space-y-2">
                  {insight.bullets.map((bullet, idx) => (
                    <li key={idx} className="flex items-start text-xs">
                      <span className="mr-2">•</span>
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              )}
              
              {/* If it has columns (two-column layout) */}
              {insight.columns && (
                <div className="grid grid-cols-2 gap-2">
                  {insight.columns.map((column:any, idx) => (
                    <div key={idx}>
                      {column.label && (
                        <p className="text-xs font-bold mb-1">{column.label}</p>
                      )}
                      {column.subLabel && (
                        <p className="text-xs mb-1">{column.subLabel}</p>
                      )}
                      <ul className="space-y-1">
                        {column.items.map((item:any, itemIdx:number) => (
                          <li key={itemIdx} className="flex items-start text-xs">
                            <span className="mr-2">•</span>
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Right Column - Donut Chart */}
        <div className="flex flex-col items-center justify-start pt-4">
          <h2 className="text-base font-bold mb-10 text-center">
            {pageData.donutChart.heading}
          </h2>
          
          <div className="relative w-[350px] h-[350px]">
            <Doughnut data={donutData} options={donutOptions} />
            
            {/* Labels positioned around the chart */}
            <div className="absolute top-16 right-8 text-center">
              <p className="text-sm font-bold">YES</p>
              <p className="text-lg font-bold">{pageData.donutChart.data.yes}%</p>
            </div>
            
            <div className="absolute bottom-16 left-8 text-center">
              <p className="text-sm font-bold">NO</p>
              <p className="text-lg font-bold">{pageData.donutChart.data.no}%</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}